/**
 * Форматирование значений для интерфейса (SK-6). UI на русском, форматы — по ТЗ и
 * CLAUDE.md правило 8: даты "ДД.ММ.ГГГГ", деньги с разрядами и знаком рубля.
 *
 * Домен и API оперируют календарной строкой `ГГГГ-ММ-ДД` (правило 3, Р-4) и деньгами строкой
 * (правило 2, без float) — здесь только перекладка в человекочитаемый вид, без Date и таймзон.
 */

/**
 * Неразрывный пробел (U+00A0). Задаём кодом, а не литералом: правило no-irregular-whitespace
 * запрещает невидимый символ в исходнике. Число и знак валюты/процента с ним не переносятся
 * по строке (русская типографика).
 */
const NBSP = String.fromCharCode(160);

/**
 * Деньги: каноничная строка "1840000.5" (из API, копейки через Decimal) в вид с разрядами и
 * рублём (правило 8). Без float: разряды группируем строкой, дробную часть дополняем до 2 знаков.
 * Разделители разрядов и перед рублём — неразрывный пробел. Пусто/некорректно -> "—".
 * Отрицательные (на будущее — экспозиция) — с минусом.
 */
export function formatMoney(value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  const negative = value.startsWith("-");
  const abs = negative ? value.slice(1) : value;
  const [intPart = "0", fracRaw = ""] = abs.split(".");
  if (!/^\d+$/.test(intPart)) return "—";
  const frac = (fracRaw + "00").slice(0, 2);
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${negative ? "−" : ""}${grouped},${frac}${NBSP}₽`;
}

/**
 * Подпись ДЕНЕЖНОЙ ОСИ графика: целые копейки → короткая надпись в рублях («15 млн ₽»).
 *
 * Зачем отдельно от `formatMoney`. Полная сумма с копейками и разрядами («15 000 000,00 ₽») на
 * делениях оси не помещается: подписей на оси пять-семь, они стоят вплотную и наезжают друг на
 * друга. Поэтому здесь — сокращение до миллионов и тысяч, а точные суммы человек читает в списке
 * под графиком и во всплывающей подсказке, где они форматируются `formatMoney` из строки-денег.
 *
 * ОКРУГЛЕНИЕ ЗДЕСЬ ОСОЗНАННОЕ И БЕЗОПАСНОЕ: это надпись на оси, а не сумма. Ни одно значение,
 * прошедшее через эту функцию, не показывается как денежный итог и не участвует в расчётах —
 * правило 2 нарушено не будет (оно про деньги, а не про засечки координатной сетки).
 *
 * Вход — ЦЕЛЫЕ копейки (так задана длина столбика), поэтому в рубли переводим целочисленно.
 */
export function formatMoneyAxis(kopecks: number): string {
  if (!Number.isFinite(kopecks)) return "";
  const negative = kopecks < 0;
  const rubles = Math.round(Math.abs(kopecks) / 100);
  const sign = negative ? "−" : "";

  if (rubles >= 1_000_000) {
    // Один знак после запятой: «1,5 млн» отличимо от «1,4 млн», а два знака на оси уже шум.
    const millions = (rubles / 1_000_000).toFixed(1).replace(/\.0$/, "").replace(".", ",");
    return `${sign}${millions}${NBSP}млн${NBSP}₽`;
  }
  if (rubles >= 10_000) {
    return `${sign}${Math.round(rubles / 1000)}${NBSP}тыс${NBSP}₽`;
  }
  return `${sign}${rubles}${NBSP}₽`;
}

/**
 * Ставка неустойки, % в день: каноничная строка "0.1" (из API) в "0,1 % в день" (правило 8).
 * Точку меняем на запятую (русский разделитель); перед знаком % — неразрывный пробел. Пусто -> "—".
 */
export function formatRatePerDay(value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  return `${value.replace(".", ",")}${NBSP}% в день`;
}

/**
 * Доля в процентах — каноничная строка из API («10», «12.5») в «10 %», «12,5 %» (правило 8).
 * Отдельно от `formatRatePerDay`: та дописывает «в день», а потолок неустойки (CR-2а) — это доля
 * от суммы, а не дневная ставка.
 */
export function formatPercent(value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  return `${value.replace(".", ",")}${NBSP}%`;
}

/**
 * Ставка из расчётного ядра — СОТЫЕ ДОЛИ процента в день (10 → «0,1 % в день»), правило 8.
 * Строковая математика: делим на 100 сдвигом запятой, без float. Нужна карточке расчёта (E9),
 * где ставка приходит целым числом, а не строкой договора (для неё — `formatRatePerDay` выше).
 */
export function formatRateHundredths(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${splitHundredths(value)}${NBSP}% в день`;
}

/**
 * Ключевая ставка ЦБ — сотые доли процента ГОДОВЫХ (1425 → «14,25 % годовых»), правило 8.
 * Так ставка хранится в справочнике (E3) и приходит в периодах расчёта по ст. 395 (E9).
 */
export function formatPercentAnnual(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${splitHundredths(value)}${NBSP}% годовых`;
}

/**
 * Целые сотые доли процента → десятичная запись с запятой и без хвостовых нулей:
 * 1600 → «16», 1550 → «15,5», 1425 → «14,25», 5 → «0,05». Строкой, чтобы не ловить
 * двоичную погрешность (1425/100 в float даёт 14.25, но 829/100 — 8.290000000000001).
 */
function splitHundredths(value: number): string {
  const negative = value < 0;
  const abs = Math.abs(Math.trunc(value)).toString().padStart(3, "0");
  const whole = abs.slice(0, -2);
  const frac = abs.slice(-2).replace(/0+$/, "");
  const sign = negative ? "−" : "";
  return frac === "" ? `${sign}${Number(whole)}` : `${sign}${Number(whole)},${frac}`;
}

/** `2026-05-14` → `14.05.2026`. Пустую/некорректную строку показываем как «—» (нет данных). */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return "—";
  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
}

/** Часовой пояс организации для показа моментов времени (Р-16, Asia/Omsk = UTC+6). */
export const ORG_TIME_ZONE = "Asia/Omsk";

/**
 * Момент времени (ISO timestamptz) → `ДД.ММ.ГГГГ ЧЧ:ММ` в поясе организации (Р-16, правило 4:
 * все времена показываем по поясу Предприятия, а не по UTC/поясу браузера). Нужен журналу
 * аудита: «когда» действия юрист читает в омском времени. Некорректный вход — «—».
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("ru-RU", {
    timeZone: ORG_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(at);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("day")}.${get("month")}.${get("year")} ${get("hour")}:${get("minute")}`;
}

/**
 * Момент времени → `ЧЧ:ММ` в поясе организации. Нужен там, где день и так очевиден из
 * окружающего текста: «в работе у Петрова с 14:20» (D-24). Полная дата в такой строке читалась бы
 * как «взял в работу когда-то давно», хотя захват живёт час.
 */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    timeZone: ORG_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(at);
}
